import { superstructResolver } from '@hookform/resolvers/superstruct';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { conditionalPaymentValidationSchema } from '../../types/formValidators';
import { PaymentFormType } from '../../types/forms';
import { SafeChargeCardFieldType } from '../../types/safeCharge';

import {
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Modal,
    Option,
    Select,
    Sheet,
    Typography,
} from '@mui/joy';
import { Controller } from 'react-hook-form';
import { StyledButton } from '../common/styled/ActionablesJoy.styled';

import { CountryRegionData } from 'react-country-region-selector';
import { useHistory } from 'react-router-dom';
import {
    AnswersQuestionnaireDocument,
    CreateCheckoutSessionDocument,
    CreateInternalOrderInput,
    CreateOrderDocument,
    PaymentPageEventQuery,
    QuestionnaireAnswerInput,
} from '../../graphql/graphql';
import { margin2, margin3 } from '../../utils/sharedStyleVariables';
import { SafeChargeCC } from '../orderSummary/safeCharge.component';
//import { sendPurchaseDataToAnalytics } from '../../utils/googleTagManager.ts';
import { useApolloClient } from '@apollo/client';
import { SafeChargeContext } from '../common/safecharge.provider';
import { CartContext } from '../providers/cart.tsx';

interface PaymentFormProps {
    setTermsOfServiceShowing: Dispatch<SetStateAction<boolean>>;
    organizerPrimaryColor: string;
    event: PaymentPageEventQuery['event'];
    setErrorAlert: (err: string) => void;
    total: number;
    loading: boolean;
    setLoading: (status: boolean) => void;
    onContinueClick?: () => void;
    hasQuestionnaire: boolean;
}

export type paymentFieldType = {
    cardHN: string;
};

//language=GraphQL
export default function PaymentForm({
    setErrorAlert,
    organizerPrimaryColor,
    event,
    total,
    loading,
    setLoading,
    onContinueClick,
    hasQuestionnaire,
}: PaymentFormProps) {
    const { t } = useTranslation();
    const history = useHistory();
    const safeCharge = useContext(SafeChargeContext);

    const [paymentFields, setPaymentFields] = useState<paymentFieldType>({
        cardHN: '',
    });
    const [safeChargeCardField, setSafeChargeCardField] = useState<SafeChargeCardFieldType | null>(
        null
    );
    const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
    const { cart, setProcessedOrderId } = useContext(CartContext);

    const apollo = useApolloClient();

    const methods = useForm({
        defaultValues: {
            firstName: '',
            firstNameConfirm: '',
            lastName: '',
            streetAddress: '',
            city: '',
            country: '',
            province: '',
            zipCode: '',
            phoneNumber: '',
            email: '',
            emailConfirmation: '',
        },
        resolver: superstructResolver(
            conditionalPaymentValidationSchema(event?.requirePurchaserAddress, t)
        ),
        mode: 'onBlur',
    });

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
        watch,
    } = methods;

    const country = watch('country');

    // Helper function to translate payment error messages
    const translatePaymentError = (errorMessage: string | undefined | null): string => {
        if (!errorMessage) return t('payment.errors.error', 'Error: {{errorMessage}}', { errorMessage: '' });

        const lowerMessage = errorMessage.toLowerCase().trim();

        // Map common payment error messages to translation keys
        const errorMap: Record<string, string> = {
            'payment declined': 'payment.errors.paymentDeclined',
            'no precheckout hold found': 'payment.errors.noPrecheckoutHold',
            'failed to create checkout session': 'payment.errors.failedToCreateCheckoutSession',
            'card information needs to be filled out': 'payment.errors.cardInformationRequired',
            'invalid canadian postal code': 'payment.errors.invalidCanadianPostalCode',
            'invalid us zip code': 'payment.errors.invalidUSZipCode',
            'credit card company not supported': 'payment.errors.cardNotSupported',
            'please complete payment fields': 'payment.errors.completePaymentFields',
            'please complete expiry date': 'payment.errors.completeExpiryDate',
            'please submit a complete cvv number': 'payment.errors.completeCVV',
        };

        // Check for exact matches first
        const exactMatch = errorMap[lowerMessage];
        if (exactMatch) {
            return t(exactMatch);
        }

        // Check for partial matches
        for (const [key, translationKey] of Object.entries(errorMap)) {
            if (lowerMessage.includes(key)) {
                return t(translationKey);
            }
        }

        // If no translation found, return the translated error template with the original message
        return t('payment.errors.error', 'Error: {{errorMessage}}', { errorMessage });
    };

    const handleSubmitPayment: SubmitHandler<PaymentFormType> = async (data) => {
        setLoading(true);

        try {
            if (!cart?.hold?.id) {
                return displayError(t('payment.errors.noPrecheckoutHold', 'No precheckout hold found. Please try again.'));
            }

            if (total > 0 && (paymentFields.cardHN === '' || !safeChargeCardField)) {
                return displayError(t('payment.errors.cardInformationRequired', 'Card information needs to be filled out.'));
            }

            if (
                data.country === 'CA' &&
                !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(data.zipCode.trim())
            ) {
                return displayError(t('payment.errors.invalidCanadianPostalCode', 'Invalid Canadian postal code.'));
            }

            if (data.country === 'US' && !/^\d{5}(-\d{4})?$/.test(data.zipCode.trim())) {
                return displayError(t('payment.errors.invalidUSZipCode', 'Invalid US zip code.'));
            }

            // Get Payment information if there
            let paymentRequest = null;

            if (safeChargeCardField && safeCharge) {
                const sessionResponse = await apollo.mutate({
                    mutation: CreateCheckoutSessionDocument,
                });

                if (!sessionResponse.data?.createCheckoutSession?.sessionToken) {
                    return displayError(t('payment.errors.failedToCreateCheckoutSession', 'Failed to Create a Checkout Session'));
                }

                const res = await safeCharge.getToken(safeChargeCardField, {
                    sessionToken: sessionResponse.data.createCheckoutSession.sessionToken,
                    cardHolderName: paymentFields.cardHN,
                    billingAddress: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        country: data.country,
                    },
                });

                if (res.status === 'SUCCESS') {
                    paymentRequest = {
                        nonce: res.ccTempToken,
                        sessionToken: sessionResponse.data.createCheckoutSession.sessionToken,
                        clientRequestId: idempotencyKey,
                        cardHolderName: paymentFields.cardHN,
                    };
                }

                if (res.error) {
                    window.scrollTo(0, 0);
                    if (res.error.id === 'incomplete_card_number') {
                        displayError(t('payment.errors.completePaymentFields', 'Please complete payment fields in order to proceed'));
                    }

                    if (res.error.id === 'incomplete_date') {
                        displayError(t('payment.errors.completeExpiryDate', 'Please complete expiry date'));
                    }

                    if (res.error.id === 'incomplete_cvc') {
                        displayError(t('payment.errors.completeCVV', 'Please submit a complete CVV number'));
                    }

                    displayError(t('payment.errors.paymentDeclined', 'Payment declined. Please try another card'));

                    return;
                }

                if (res.status === 'ERROR' && res.error.type === 'card') {
                    displayError(
                        t('payment.errors.cardNotSupported', 'Credit card company not supported by processor, please try a different card')
                    );
                    return;
                }
            }

            const body: CreateInternalOrderInput = {
                profile: {
                    firstName: data.firstName,
                    firstNameConfirm: data.firstNameConfirm,
                    lastName: data.lastName,
                    email: data.email,
                    phone: data.phoneNumber,
                    streetAddress: data.streetAddress,
                    city: data.city,
                    province: data.province,
                    postalCode: data.zipCode,
                    country: data.country,
                },
                hold: {
                    id: cart.hold.id,
                    fingerprint: cart?.hold?.visitorId as string,
                },
                idempotencyKey,
                referral: cart?.referralCodes?.[0] ?? null,
                paymentRequest,
            };

            const response = await apollo.mutate({
                mutation: CreateOrderDocument,
                variables: {
                    payload: body,
                },
            });

            if (response?.data?.createInternalOrder) {
                setProcessedOrderId(response.data.createInternalOrder);

                if (onContinueClick) {
                    void onContinueClick();
                }

                if (hasQuestionnaire) {
                    const questionAnswers: QuestionnaireAnswerInput[] =
                        cart?.questionnaireAnswers.map((q) => {
                            return {
                                question: q.question,
                                answer: [...q.answer.split(',')],
                            };
                        });

                    const questionnaireResponse = await apollo.mutate({
                        mutation: AnswersQuestionnaireDocument,
                        variables: {
                            orderShortId: response.data.createInternalOrder,
                            submissionInput: {
                                questionnaireVersion: event?.questionnaire?.version ?? 1,
                                answers: questionAnswers,
                            },
                        },
                    });

                    if (
                        !questionnaireResponse.data?.submitQuestionnaireAnswers
                            ?.questionnaireVersion
                    ) {
                        alert(
                            t('payment.errors.questionnaireSubmissionFailed', 'Payment Successful, questionnaire submission failed. Please contact organizer to complete questionnaire.')
                        );
                    }

                    history.push('/confirmation');
                } else {
                    history.push('/confirmation');
                }
            }
        } catch (error) {
            console.error((error as Error)?.message);
            window.scrollTo(0, 0);

            if ((error as Error)?.message.toLowerCase().includes('payment declined')) {
                setIdempotencyKey(uuidv4());
            }

            setErrorAlert(translatePaymentError((error as Error)?.message));
        } finally {
            setLoading(false);
        }
    };

    const displayError = (text: string) => {
        window.scrollTo(0, 0);
        setErrorAlert(text);
    };

    const canadaData = CountryRegionData.find(([countryName]) => countryName === 'Canada');
    const usData = CountryRegionData.find(([countryName]) => countryName === 'United States');
    const otherCountriesData = CountryRegionData.filter(
        ([countryName]) => countryName !== 'Canada' && countryName !== 'United States'
    );
    const orderedCountryData = [canadaData!, usData!, ...otherCountriesData];

    const inputStyle = {
        '&:focus-within::before': {
            boxShadow: `inset 0px 0px 1px 1px ${organizerPrimaryColor}!important`,
        },
    };

    const requiredSymbol = () => {
        return <span style={{ color: 'rgba(196, 28, 28, 1)' }}>*</span>;
    };

    return (
        <Box sx={{ flex: '1' }}>
            <form onSubmit={handleSubmit(handleSubmitPayment)}>
                <Box>
                    <Typography
                        level={'body-lg'}
                        sx={{
                            fontSize: 24,
                            mb: margin2,
                            textAlign: 'left',
                            fontWeight: 600,
                        }}
                    >
                        {t('payment.yourInformation', 'Your Information')}
                    </Typography>
                </Box>
                <Typography
                    level={'body-md'}
                    sx={{
                        mb: margin3,
                        textAlign: 'left',
                        fontWeight: 600,
                    }}
                >
                    {t('payment.fullName', 'Full name')}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        marginBottom: 3,
                        flexDirection: { md: 'row', sm: 'column', xs: 'column' },
                    }}
                >
                    {/* First Name */}
                    <Box>
                        <FormControl error id="firstName" sx={{ marginBottom: 1 }}>
                            <>
                                <FormLabel sx={{ fontWeight: 500 }}>
                                    {t('payment.firstName', 'First Name')}{requiredSymbol()}
                                </FormLabel>
                                <Input
                                    color="neutral"
                                    placeholder={t('payment.firstName', 'First Name')}
                                    size="md"
                                    fullWidth
                                    sx={{ ...inputStyle }}
                                    {...register('firstName')}
                                    error={!!errors.firstName}
                                />
                                {errors.firstName && (
                                    <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                        {errors?.firstName?.message ? errors.firstName.message : ''}
                                    </FormHelperText>
                                )}
                            </>
                        </FormControl>
                    </Box>
                    {/* Last Name */}
                    <Box sx={{ flex: 1 }}>
                        <FormControl error id="lastName" sx={{ width: '100%', marginBottom: 1 }}>
                            <>
                                <FormLabel sx={{ fontWeight: 500 }}>
                                    {t('payment.lastName', 'Last Name')}{requiredSymbol()}
                                </FormLabel>
                                <Input
                                    color="neutral"
                                    placeholder={t('payment.lastName', 'Last Name')}
                                    size="md"
                                    fullWidth
                                    sx={{ ...inputStyle }}
                                    {...register('lastName')}
                                    error={!!errors.lastName}
                                />
                                {errors.lastName && (
                                    <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                        {errors?.lastName?.message ? errors.lastName.message : ''}
                                    </FormHelperText>
                                )}
                            </>
                        </FormControl>
                    </Box>
                </Box>

                <Typography
                    level={'body-md'}
                    sx={{
                        mt: margin3,
                        mb: margin3,
                        textAlign: 'left',
                        fontWeight: 600,
                    }}
                >
                    {t('payment.address', 'Address')}
                </Typography>
                {/* Street Address*/}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControl error id="streetAddress" sx={{ width: '100%', marginBottom: 1 }}>
                        <>
                            <FormLabel sx={{ fontWeight: 500 }}>
                                {t('payment.streetAddress', 'Street Address')}
                                {event?.requirePurchaserAddress ? requiredSymbol() : ''}
                            </FormLabel>
                            <Input
                                color="neutral"
                                placeholder={t('payment.streetAddress', 'Street Address')}
                                size="md"
                                fullWidth
                                sx={{ ...inputStyle }}
                                {...register('streetAddress')}
                                error={!!errors.streetAddress}
                            />
                            {errors.streetAddress && (
                                <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                    {errors?.streetAddress?.message
                                        ? errors.streetAddress.message
                                        : ''}
                                </FormHelperText>
                            )}
                        </>
                    </FormControl>
                </Box>

                {/* Town City */}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControl error id="city" sx={{ width: '100%', marginBottom: 1 }}>
                        <>
                            <FormLabel sx={{ fontWeight: 500 }}>
                                {t('payment.townCity', 'Town / City')}{event?.requirePurchaserAddress ? requiredSymbol() : ''}
                            </FormLabel>
                            <Input
                                color="neutral"
                                placeholder={t('payment.city', 'City')}
                                size="md"
                                fullWidth
                                sx={{ ...inputStyle }}
                                {...register('city')}
                                error={!!errors.city}
                            />
                            {errors.city && (
                                <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                    {errors?.city?.message ? errors.city.message : ''}
                                </FormHelperText>
                            )}
                        </>
                    </FormControl>
                </Box>
                {/* Addresss block grid..................... */}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControl error id="country" sx={{ marginBottom: 1 }}>
                        <FormLabel>{t('payment.country', 'Country')}{requiredSymbol()}</FormLabel>
                        <Controller
                            name="country"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    id="country"
                                    color="neutral"
                                    placeholder={t('payment.country', 'Country')}
                                    size="md"
                                    value={watch('country')}
                                    defaultValue={''}
                                    onChange={(_, newValue) => {
                                        setValue('country', newValue as string);
                                    }}
                                >
                                    <Option value="">{t('payment.selectCountry', '--Select Country--')}</Option>
                                    {orderedCountryData.map(([countryName, countryCode]) => (
                                        <Option key={countryCode} value={countryCode}>
                                            {countryName}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.country && errors.country.message && (
                            <FormHelperText sx={{ color: 'red' }}>
                                {errors.country.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        marginBottom: 3,
                        flexDirection: { md: 'row', sm: 'column', xs: 'column' },
                    }}
                >
                    <Box sx={{ marginBottom: 2, flex: 1 }}>
                        <FormControl error id="province" sx={{ marginBottom: 1 }}>
                            <FormLabel>
                                {country === 'US' ? (
                                    <>{t('payment.state', 'State')}{requiredSymbol()}</>
                                ) : country === 'CA' ? (
                                    <>{t('payment.province', 'Province')}{requiredSymbol()}</>
                                ) : (
                                    <>
                                        {t('payment.stateProvince', 'State/Province')}
                                        {event?.requirePurchaserAddress ? requiredSymbol() : ''}
                                    </>
                                )}
                            </FormLabel>
                            <Controller
                                name="province"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        id="province"
                                        color="neutral"
                                        placeholder={t('payment.province', 'Province')}
                                        sx={{ width: '100%' }}
                                        size="md"
                                        value={watch('province')}
                                        defaultValue={''}
                                        onChange={(_, newValue) => {
                                            setValue('province', newValue as string);
                                        }}
                                    >
                                        <Option value="">{t('payment.selectProvinceState', '--Select Province/State--')}</Option>

                                        {country !== '' &&
                                            (country !== undefined || true) &&
                                            // @ts-expect-error - For now.
                                            CountryRegionData.find(
                                                ([, countryCode]) => countryCode === country
                                            )[2]

                                                .split('|')
                                                .map((region) => {
                                                    const [name] = region.split('~');
                                                    return (
                                                        <Option key={name} value={name}>
                                                            {name}
                                                        </Option>
                                                    );
                                                })}
                                    </Select>
                                )}
                            />
                            {errors.province && errors.province.message && (
                                <FormHelperText sx={{ color: 'red' }}>
                                    {errors.province.message ===
                                    'Expected a string, but received: null'
                                        ? t('payment.validation.provinceStateRequired')
                                        : errors.province.message}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    {/* Zip */}
                    <Box sx={{ marginBottom: 2, flex: 1 }}>
                        <FormControl
                            error
                            id="zipCode"
                            required
                            sx={{ width: '100%', marginBottom: 1 }}
                        >
                            <>
                                <FormLabel sx={{ fontWeight: 500 }}>{t('payment.postalCodeZipCode', 'Postal Code/ZIP Code')}</FormLabel>
                                <Input
                                    color="neutral"
                                    placeholder={t('payment.postalCodeZipCode', 'Postal Code/ZIP Code')}
                                    size="md"
                                    fullWidth
                                    sx={{ ...inputStyle }}
                                    {...register('zipCode')}
                                    error={!!errors.zipCode}
                                />
                                {errors.zipCode && (
                                    <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                        {errors?.zipCode?.message ? errors.zipCode.message : ''}
                                    </FormHelperText>
                                )}
                            </>
                        </FormControl>
                    </Box>
                </Box>

                <Typography
                    level={'body-md'}
                    sx={{
                        mb: margin3,
                        textAlign: 'left',
                        fontWeight: 600,
                    }}
                >
                    {t('payment.contactInformation', 'Contact Information')}
                </Typography>
                {/* Phone */}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControl error id="phoneNumber" sx={{ width: '100%', marginBottom: 1 }}>
                        <>
                            <FormLabel sx={{ fontWeight: 500 }}>
                                {t('payment.phoneNumber', 'Phone Number')}{requiredSymbol()}
                            </FormLabel>
                            <Input
                                color="neutral"
                                placeholder={t('payment.phoneNumber', 'Phone Number')}
                                size="md"
                                fullWidth
                                sx={{ ...inputStyle }}
                                {...register('phoneNumber')}
                                error={!!errors.phoneNumber}
                            />
                            {errors.phoneNumber && (
                                <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                    {errors?.phoneNumber?.message ? errors.phoneNumber.message : ''}
                                </FormHelperText>
                            )}
                        </>
                    </FormControl>
                </Box>
                {/* Email */}
                <Box sx={{ marginBottom: 2 }}>
                    <FormControl error id="email" sx={{ width: '100%', marginBottom: 1 }}>
                        <>
                            <FormLabel sx={{ fontWeight: 500 }}>{t('payment.email', 'Email')}{requiredSymbol()}</FormLabel>
                            <Input
                                color="neutral"
                                placeholder={t('payment.email', 'Email')}
                                size="md"
                                fullWidth
                                sx={{ ...inputStyle }}
                                {...register('email')}
                                error={!!errors.email}
                            />
                            {errors.email && (
                                <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                    {errors?.email?.message ? errors.email.message : ''}
                                </FormHelperText>
                            )}
                        </>
                    </FormControl>
                </Box>
                {/* Email Confirm */}
                <Box>
                    <FormControl
                        error
                        id="emailConfirmation"
                        sx={{ width: '100%', marginBottom: 1 }}
                    >
                        <>
                            <FormLabel sx={{ fontWeight: 500 }}>
                                {t('payment.confirmEmail', 'Confirm Email')}{requiredSymbol()}
                            </FormLabel>
                            <Input
                                color="neutral"
                                placeholder={t('payment.confirmEmail', 'Confirm Email')}
                                size="md"
                                fullWidth
                                sx={{ ...inputStyle }}
                                {...register('emailConfirmation')}
                                error={!!errors.emailConfirmation}
                            />
                            {errors.emailConfirmation && (
                                <FormHelperText sx={{ color: 'red', mx: 1 }}>
                                    {errors?.emailConfirmation?.message
                                        ? errors.emailConfirmation.message
                                        : ''}
                                </FormHelperText>
                            )}
                        </>
                    </FormControl>
                </Box>

                {total > 0 && (
                    <SafeChargeCC
                        paymentFields={paymentFields}
                        setSafeChargeCardField={setSafeChargeCardField}
                        setPaymentFields={setPaymentFields}
                        organizerPrimaryColor={organizerPrimaryColor}
                    />
                )}
                <Box>
                    {/* Submit button................. */}
                    <StyledButton
                        id={'place_order_button'}
                        disabled={loading}
                        type="submit"
                        backgroundcolor={organizerPrimaryColor}
                        sx={{
                            marginTop: 3,
                            width: '100%',
                        }}
                    >
                        {t('payment.placeOrder', 'Place Order')}
                    </StyledButton>
                </Box>
            </form>
            <Modal
                aria-labelledby="alert-dialog-title"
                aria-describedby="modal-alert-dialog-description"
                open={loading}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Sheet
                    variant="outlined"
                    sx={{ maxWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg' }}
                >
                    <Typography
                        component="h2"
                        id="modal-title"
                        level="title-lg"
                        textColor="inherit"
                        sx={{ mb: 2 }}
                    >
                        {t('payment.processing', 'Processing...')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress sx={{ color: organizerPrimaryColor }} />
                    </Box>
                    <Typography id="alert-dialog-description" level="body-lg">
                        {t('payment.processingMessage', 'We are processing your order. Please do not close or refresh this browser tab.')}
                    </Typography>
                </Sheet>
            </Modal>
        </Box>
    );
}
